import { useState, useEffect, useRef } from "react";
import SectionHeading from "../components/SectionHeading";
import { galleryPhotos } from "../data/gallery";
import "./Gallery.css";

export default function Gallery() {
  const totalMobileSlides = galleryPhotos.length + 1;

  const [isMobile, setIsMobile] = useState(false);
  const [desktopCurrent, setDesktopCurrent] = useState(0);
  const [mobileCurrent, setMobileCurrent] = useState(0);

  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 580px)");

    const updateScreen = () => setIsMobile(mediaQuery.matches);

    updateScreen();
    mediaQuery.addEventListener("change", updateScreen);

    return () => {
      mediaQuery.removeEventListener("change", updateScreen);
    };
  }, []);

  const nextDesktopSlide = () => {
    setDesktopCurrent((prev) => (prev + 1) % 2);
  };

  const previousDesktopSlide = () => {
    setDesktopCurrent((prev) => (prev - 1 + 2) % 2);
  };

  const nextMobileSlide = () => {
    setMobileCurrent((prev) => (prev + 1) % totalMobileSlides);
  };

  const previousMobileSlide = () => {
    setMobileCurrent(
      (prev) => (prev - 1 + totalMobileSlides) % totalMobileSlides
    );
  };

  const handleTouchStart = (event) => {
    touchStartX.current = event.touches[0].clientX;
    touchStartY.current = event.touches[0].clientY;
  };

  const handleTouchEnd = (event) => {
    const deltaX =
      event.changedTouches[0].clientX - touchStartX.current;
    const deltaY =
      event.changedTouches[0].clientY - touchStartY.current;

    if (
      Math.abs(deltaX) > 45 &&
      Math.abs(deltaX) > Math.abs(deltaY)
    ) {
      if (deltaX < 0) {
        nextMobileSlide();
      } else {
        previousMobileSlide();
      }
    }
  };

  return (
    <section id="gallery" className="section">
      <div className="container">
        <SectionHeading plain="Our" accent="Gallery" />

        <div className="gallery-slider">
          {/* Previous Arrow */}
          <button
            className="gallery-slider__arrow gallery-slider__arrow--left"
            onClick={
              isMobile ? previousMobileSlide : previousDesktopSlide
            }
            aria-label="Previous slide"
          >
            <img src="/images/left.png" alt="Previous" />
          </button>

          {/* Gallery Content */}
          <div
            className="gallery-slider__content"
            onTouchStart={isMobile ? handleTouchStart : undefined}
            onTouchEnd={isMobile ? handleTouchEnd : undefined}
          >
            {isMobile ? (
              /* Mobile: One photo per slide, followed by video */
              <div className="gallery gallery--single">
                {mobileCurrent < galleryPhotos.length ? (
                  <img
                    className="gallery__single-image"
                    src={galleryPhotos[mobileCurrent].src}
                    alt={galleryPhotos[mobileCurrent].alt}
                    draggable="false"
                  />
                ) : (
                  <video
                    className="gallery__single-video"
                    src="/gallery/gallery-video.mp4"
                    controls
                    playsInline
                    preload="metadata"
                  />
                )}
              </div>
            ) : (
              /* Desktop/tablet: Original grid and video slides */
              desktopCurrent === 0 ? (
                <div className="gallery gallery--images">
                  <div className="gallery__grid">
                    {galleryPhotos.map(({ src, alt }) => (
                      <img
                        key={src}
                        src={src}
                        alt={alt}
                        loading="lazy"
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="gallery gallery--video">
                  <video
                    src="/gallery/gallery-video.mp4"
                    controls
                    playsInline
                    preload="metadata"
                  />
                </div>
              )
            )}
          </div>

          {/* Next Arrow */}
          <button
            className="gallery-slider__arrow gallery-slider__arrow--right"
            onClick={isMobile ? nextMobileSlide : nextDesktopSlide}
            aria-label="Next slide"
          >
            <img src="/images/right.png" alt="Next" />
          </button>
        </div>

        {/* Dots */}
        <div className="gallery-slider__dots">
          {isMobile
            ? Array.from(
                { length: totalMobileSlides },
                (_, index) => (
                  <button
                    key={index}
                    className={`gallery-slider__dot ${
                      mobileCurrent === index ? "is-active" : ""
                    }`}
                    onClick={() => setMobileCurrent(index)}
                    aria-label={
                      index === galleryPhotos.length
                        ? "Show video"
                        : `Show photo ${index + 1}`
                    }
                  />
                )
              )
            : [0, 1].map((index) => (
                <button
                  key={index}
                  className={`gallery-slider__dot ${
                    desktopCurrent === index ? "is-active" : ""
                  }`}
                  onClick={() => setDesktopCurrent(index)}
                  aria-label={
                    index === 0 ? "Show image gallery" : "Show video"
                  }
                />
              ))}
        </div>
      </div>
    </section>
  );
}